import { Flex, Icon, Text, Spinner, IconButton } from '@chakra-ui/react';
import { ReactComponent as UploadIcon } from 'resources/images/uploadIcon.svg';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useAppDispatch, useAppSelector } from 'app/store';
import { addAlertAction } from 'app/store/Alerts/slice';
import { useCallback, useEffect, useState } from 'react';
import {
  importLyricsEffect,
  songByTitleWildCardEffect,
} from 'app/store/Import/effects';
import { clearFileUpload, setImportSongsAction } from 'app/store/Import/slice';
import { getImportSongs, getImportStatus } from 'app/store/Import/selectors';
import { acceptSx, activeSx, baseSx, closeBtnSx, rejectSx } from '../styles';
import { ImportSongsTable } from './ImportSongsTable';
import { ImportLyrics, UploadStatus } from '../types';
import {
  filesToImportSongs,
  getMessageByStatus,
  isSongsFolderHasCorrectStructure,
  readCSV,
} from '../utils';
import { useUploadAudio } from './useUploadAudio';
import { ImportLoader } from '../ImportLoader';

export const ImportSongs = () => {
  const dispatch = useAppDispatch();
  const songs = useAppSelector(getImportSongs);
  const importStatus = useAppSelector(getImportStatus);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const message = getMessageByStatus(importStatus);
    if (message) {
      dispatch(addAlertAction({ message }));
    }
  }, [dispatch, importStatus]);

  const { uploadAudio } = useUploadAudio();

  const uploadLyrics = useCallback(
    async ({ file }: ImportLyrics, songTitle: string, songGuid?: string) => {
      readCSV(file, async (text) => {
        dispatch(importLyricsEffect({ songGuid, songTitle, text }));
      });
    },
    [dispatch]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    noClick: true,
    onDropRejected: (rejections: FileRejection[]) => {
      rejections.forEach((rejection) => {
        rejection.errors.forEach((error) => {
          if (error.code === 'file-invalid-type') {
            dispatch(
              addAlertAction({
                message: `${rejection.file.name} has unsupported file type, please try another type.`,
              })
            );
          }
        });
      });
    },
    onDropAccepted: async (acceptedFiles) => {
      const isValid = isSongsFolderHasCorrectStructure(acceptedFiles);
      if (!isValid) {
        dispatch(
          addAlertAction({
            message: 'Check songs folder structure, please try another one.',
          })
        );
        return;
      }

      const data = filesToImportSongs(acceptedFiles);
      if (data.length === 0) {
        dispatch(
          addAlertAction({
            message: 'No songs were found to import',
          })
        );
        return;
      }

      setIsLoading(true);

      const getDefaultStatus = (guid?: string): UploadStatus =>
        guid ? 'loading' : 'failed';

      const songs = await Promise.all(
        data.map(async (importSong) => {
          const song = await dispatch(
            songByTitleWildCardEffect({
              titleWildcard: importSong.songTitle.replaceAll('_', '?'),
            })
          ).unwrap();
          return Promise.resolve({
            guid: song?.guid,
            ...importSong,
            audios: importSong.audios.map((audio) => ({
              ...audio,
              status: getDefaultStatus(song?.guid),
            })),
          });
        })
      );

      dispatch(
        setImportSongsAction(
          songs.map(({ audios, lyrics, ...song }) => {
            return {
              ...song,
              ...(lyrics
                ? {
                    lyrics: {
                      status: getDefaultStatus(song?.guid),
                    },
                  }
                : {}),
              audios: audios.map(({ file, ...audio }) => audio),
            };
          })
        )
      );

      songs
        .filter(({ guid }) => guid)
        .forEach((song) => {
          song.audios.forEach((audio) => {
            uploadAudio(audio, song.guid);
          });
          if (song.lyrics) {
            uploadLyrics(song.lyrics, song.songTitle, song.guid);
          }
        });

      setIsLoading(false);
    },
  });

  const done = songs.filter(
    ({ audios, lyrics }) =>
      !lyrics ||
      (lyrics.status === 'uploaded' && audios.length === 0) ||
      !audios.some(({ status }) => status === 'loading')
  ).length;

  if (!isLoading && songs.length > 0) {
    return (
      <>
        <ImportLoader inProgress={done} count={songs.length} my={2} />
        <IconButton
          variant="secondary"
          aria-label="close"
          title='Clear "Import songs" list'
          size="xs"
          sx={closeBtnSx}
          onClick={() => {
            dispatch(clearFileUpload());
          }}
          icon={<CloseIcon />}
        />
        <ImportSongsTable songs={songs} />
      </>
    );
  }

  return (
    <Flex
      sx={{
        ...baseSx,
        ...(isDragActive ? activeSx : {}),
        ...(isDragAccept ? acceptSx : {}),
        ...(isDragReject ? rejectSx : {}),
      }}
      {...getRootProps()}
      data-testid="dragAndDrop-import-songs"
    >
      {isLoading ? (
        <Spinner size="lg" />
      ) : (
        <>
          <input {...getInputProps()} />
          <Icon
            as={UploadIcon}
            color="P.50"
            flexShrink={0}
            height="86px"
            width="86px"
          />
          <Text
            fontSize="lg"
            fontWeight={500}
            color="M.70"
            mt={7}
            textAlign="center"
          >
            Drag and drop your Songs folder here
          </Text>
        </>
      )}
    </Flex>
  );
};
